/**
 * The one Back control for the whole dashboard.
 *
 * Every page used to roll its own: `variant="text" size="small"` here,
 * `variant="outlined"` there, a faint 12px `← Back` MuiLink on a third — so on
 * most detail pages Back disappeared against the page background (reported
 * 2026-08-28). This component is deliberately *prominent*: a filled, bordered,
 * shadowed pill at a comfortable 40px tap target. Use it for EVERY standalone
 * back affordance; don't hand-roll another `← Back` link or
 * `startIcon={<ArrowLeft />}` button.
 *
 * It lived at `crm/components/back-button.tsx` until 2026-08-29 and so was
 * reachable only from the walled CRM sub-app; the rest of the dashboard kept
 * its faint text links. It now sits in the shared `components/ui/` layer — CRM
 * included, via the usual `@/components/ui/*` specifier.
 *
 * CORNER RADIUS: none of its own. It inherits `theme.shape.borderRadius` (the
 * ONE global radius knob in `styles/theme.ts`) the same way Card, TextField and
 * every other Button do. It used to hardcode `borderRadius: 2` — i.e. 2 ×
 * RADIUS = 16px — which read as an odd over-rounded pill next to the 8px cards
 * it sat above. Do not re-add a radius here: re-round the app from `theme.ts`.
 *
 * Three ways to say where "back" goes:
 *   <BackButton />                                  → history back
 *   <BackButton to="/crm/customers" />              → a specific route
 *   <BackButton onClick={fn} label="Back to List" /> → custom handler
 */
import { Button } from "@mui/material"
import type { ButtonProps } from "@mui/material"
import { ArrowLeft } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"

export interface BackButtonProps extends Omit<ButtonProps, "onClick" | "variant" | "children"> {
  /** Route to navigate to. Omit for plain history-back. */
  to?: string
  /** Custom handler. Takes precedence over `to`. */
  onClick?: () => void
  /** Button text. Defaults to "Back". */
  label?: string
}

export function BackButton({ to, onClick, label = "Back", sx, ...rest }: BackButtonProps) {
  const navigate = useNavigate()

  // `to` renders a real <a> so middle-click / open-in-new-tab still work;
  // everything else is a button.
  const linkProps = !onClick && to ? ({ component: Link, to } as const) : {}
  const handleClick = onClick ?? (to ? undefined : () => navigate(-1))

  return (
    <Button
      aria-label={label}
      startIcon={<ArrowLeft size={20} strokeWidth={2.5} />}
      {...linkProps}
      {...(handleClick ? { onClick: handleClick } : {})}
      {...rest}
      sx={{
        minHeight: 40,
        px: 2,
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.2,
        flexShrink: 0,
        textTransform: "none",
        color: "text.primary",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        boxShadow: 1,
        "& .MuiButton-startIcon": { mr: 1 },
        "&:hover": {
          bgcolor: "action.hover",
          borderColor: "text.disabled",
          boxShadow: 3,
        },
        ...sx,
      }}
    >
      {label}
    </Button>
  )
}

export default BackButton
